import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { LoadingButtonState } from "ui/button";
import { TextField } from "ui/text-field";

import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { TouchpointType } from "types/touchpoint";
import { useValidateProgramTemplateName } from "containers/admin/clients/client/program-templates-page/hooks/use-validate-program-template-name";
import { useEditProgramTemplateName } from "components/modals/edit-program-template-name-modal/hooks/use-edit-program-template-name";
import {
  EditProgramTemplateNameModalProps,
  EditProgramTemplateNameFormData,
} from "components/modals/edit-program-template-name-modal/edit-program-template-name-modal.types";

import Icons from "assets";

const EditProgramTemplateNameModal = ({
  setIsOpen = () => {},
  isOpen,
}: EditProgramTemplateNameModalProps) => {
  const currentTouchpoint = useCurrentTouchpoint();
  const currentName = currentTouchpoint.name;
  const currentId = currentTouchpoint.id;
  const currentType = currentTouchpoint.type;
  const isEmail = currentType === TouchpointType.EMAIL;
  const Icon = isEmail ? Icons.Email : Icons.LandingPage;

  const { validate: validateTemplateNameUnique } = useValidateProgramTemplateName({
    enabled: isOpen,
  });

  const methods = useForm<EditProgramTemplateNameFormData>({
    defaultValues: {
      name: currentName,
    },
    mode: "onChange",
  });

  const { control, formState, reset } = methods;
  const { isValid, isDirty } = formState;

  const validateTemplateName = (value: string) =>
    validateTemplateNameUnique({ name: value, type: currentType, excludeId: currentId });

  const {
    submitState,
    handleSubmit: handleFormSubmit,
    handleCancelClick,
  } = useEditProgramTemplateName({
    currentName,
    setIsOpen,
    reset,
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({ name: currentName });
    }
  }, [isOpen, currentName, reset]);

  if (!isOpen) return null;

  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      icon={<Icon />}
      headline="Edit template name"
      form={methods}
      onSubmit={handleFormSubmit}
      submitDisabled={!isValid || !isDirty}
      submitting={submitState === LoadingButtonState.Loading}
      submitButtonText="Save changes"
      submitLoadingMessage="Saving changes...">
      <Controller
        name="name"
        control={control}
        rules={{
          required: "Template name is required",
          validate: validateTemplateName,
        }}
        render={({ field, fieldState }) => (
          <TextField
            label="Template name"
            required
            errMessage={fieldState.error?.message}
            {...field}
          />
        )}
      />
    </FormDialog>
  );
};

export default EditProgramTemplateNameModal;
